<template>
    <div class="pb-6 border-b border-gray-200 dark:border-gray-700">
        <div v-if="!isEditing">
            <div class="flex justify-between items-center">
                <div class="flex items-center">
                    <div class="flex">
                        <template v-for="i in 5" :key="i">
                            <StarIcon
                                :class="[
                                    'h-5 w-5',
                                    i <= review.rating ? 'text-yellow-400' : 'text-gray-300'
                                ]"
                            />
                        </template>
                    </div>
                    <span class="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        by {{ review.user.name }}
                    </span>
                </div>
                <span class="text-sm text-gray-500 dark:text-gray-400">
                    {{ formatDate(review.created_at) }}
                </span>
            </div>
            <p class="mt-2 text-gray-700 dark:text-gray-300">
                {{ review.comment }}
            </p>
            <div v-if="canEdit" class="flex justify-end mt-4 space-x-4">
                <button
                    @click="startEditing"
                    class="text-sm text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                    Edit
                </button>
                <button
                    @click="showDeleteConfirmation = true"
                    class="text-sm text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                >
                    Delete
                </button>
            </div>
        </div>

        <div v-else>
            <ReviewForm
                :business="business"
                :existing-review="review"
                @review-updated="handleReviewUpdated"
                @cancel="cancelEditing"
            />
        </div>

        <!-- Delete Confirmation Modal -->
        <ConfirmationModal :show="showDeleteConfirmation" @close="showDeleteConfirmation = false">
            <template #title>
                Delete Review
            </template>
            <template #content>
                Are you sure you want to delete this review? This action cannot be undone.
            </template>
            <template #footer>
                <button
                    @click="showDeleteConfirmation = false"
                    class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-gray-700 uppercase bg-white rounded-md border border-gray-300 shadow-sm transition duration-150 ease-in-out hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-25 me-3"
                >
                    Cancel
                </button>
                <button
                    @click="confirmDelete"
                    class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase bg-red-600 rounded-md border border-transparent transition duration-150 ease-in-out hover:bg-red-500 active:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    :class="{ 'opacity-25': deleteLoading }"
                    :disabled="deleteLoading"
                >
                    Delete Review
                </button>
            </template>
        </ConfirmationModal>
    </div>
</template>

<script setup>
import { StarIcon } from '@heroicons/vue/24/solid';
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useReviewStore } from '@/stores/reviewStore';
import { useToast } from 'primevue/usetoast';
import ReviewForm from '@/components/review/ReviewForm.vue';
import ConfirmationModal from '@/components/general/ConfirmationModal.vue';

const authStore = useAuthStore();
const reviewStore = useReviewStore();
const toast = useToast();
const emit = defineEmits(['review-deleted', 'review-updated']);

const props = defineProps({
    review: {
        type: Object,
        required: true
    },
    business: {
        type: Object,
        required: true
    }
});

const isEditing = ref(false);
const showDeleteConfirmation = ref(false);
const deleteLoading = ref(false);

const canEdit = computed(() => {
    return authStore.user && (
        authStore.user.id === props.review.customer_id ||
        authStore.user.role === 'admin'
    );
});

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const startEditing = () => {
    isEditing.value = true;
};

const cancelEditing = () => {
    isEditing.value = false;
};

const handleReviewUpdated = (updatedReview) => {
    isEditing.value = false;
    emit('review-updated', updatedReview);
};

const confirmDelete = async () => {
    deleteLoading.value = true;
    try {
        await reviewStore.deleteReview(props.review.id, props.business.uuid);
        emit('review-deleted', props.review.id);
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Review deleted successfully',
            life: 3000
        });
        showDeleteConfirmation.value = false;
    } catch (error) {
        console.error('Error deleting review:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete review. Please try again.',
            life: 5000
        });
    } finally {
        deleteLoading.value = false;
    }
};
</script>
